import { useLocation } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { Head } from '@_/components';
import { LocaleCodes } from '@_/static';
import { deLocalizePath } from '@_/utils';

const origin = import.meta.env.ENV_PUBLIC_FE_BASE_URL;

interface SeoTagsProps {
  canonicalPath?: string;
  description?: string;
  imageUrl?: string;
  title?: string;
  useCurrentCanonicalPath?: boolean;
  useTitleTemplate?: boolean;
}

export function SeoTags({
  canonicalPath,
  description,
  imageUrl,
  title,
  useCurrentCanonicalPath = false,
  useTitleTemplate = false
}: SeoTagsProps) {
  const { locale } = useIntl();
  const location = useLocation();

  const resolvedTitle = title?.trim()
    ? title + (useTitleTemplate ? ' | SlashHow' : '')
    : 'SlashHow';

  let canonicalUrl = '';
  if (canonicalPath?.trim()) {
    canonicalUrl = `${origin}${canonicalPath}`;
  } else if (useCurrentCanonicalPath) {
    canonicalUrl = `${origin}${location.pathname}`;
  }

  return (
    <Head>
      <meta name="robots" content="index, follow" />

      <title>{resolvedTitle}</title>

      {!!description && <meta name="description" content={description} />}
      <meta name="author" content="SlashHow" />

      {/* HrefLang alternates */}
      {!!canonicalUrl && (
        <>
          <link rel="canonical" href={canonicalUrl} />
          {LocaleCodes.map(code => (
            <link
              key={code}
              rel="alternate"
              href={
                import.meta.env.ENV_PUBLIC_FE_BASE_URL +
                `/${code}` +
                deLocalizePath(canonicalUrl, LocaleCodes)
              }
              hrefLang={code}
            />
          ))}
          <link
            rel="alternate"
            href={
              import.meta.env.ENV_PUBLIC_FE_BASE_URL + deLocalizePath(canonicalUrl, LocaleCodes)
            }
            hrefLang="x-default"
          />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SlashHow" />
      {!!description && <meta property="og:description" content={description} />}
      {!!canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content={locale} />
      {LocaleCodes.filter(code => code !== locale).map(code => (
        <meta key={code} property="og:locale:alternate" content={code} />
      ))}
      {!!imageUrl && (
        <>
          <meta property="og:image" content={imageUrl} />
          <meta property="og:image:secure_url" content={imageUrl} />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:title" content={resolvedTitle} />
      {!!description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:card" content={imageUrl ? 'summary' : 'summary_large_image'} />
      {!!imageUrl && <meta name="twitter:image" content={imageUrl} />}
    </Head>
  );
}
